import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Lock, Images } from "lucide-react";

/** Prix en francs CFA, format local : 1 500 F CFA. */
const formaterPrix = (montant) => `${Number(montant).toLocaleString("fr-FR")} F CFA`;

/**
 * Galerie interactive d'une annonce : grande image + vignettes cliquables,
 * flèches, navigation clavier et balayage tactile. La vidéo (si présente)
 * occupe la dernière position de la galerie.
 */
export const CarrouselAnnonce = ({ medias, couleur, floute, prixDes, lienAbonnement = "/abonnement", testid = "carrousel" }) => {
  const [index, setIndex] = useState(0);
  const [finExtrait, setFinExtrait] = useState(false);
  const depart = useRef(null);

  const total = medias?.length || 0;
  const aller = (i) => {
    setFinExtrait(false);
    setIndex(((i % total) + total) % total);
  };

  useEffect(() => {
    const clavier = (e) => {
      if (e.key === "ArrowRight") aller(index + 1);
      if (e.key === "ArrowLeft") aller(index - 1);
    };
    window.addEventListener("keydown", clavier);
    return () => window.removeEventListener("keydown", clavier);
  }, [index, total]);

  if (!total) {
    return (
      <div className="flex h-64 items-center justify-center rounded-2xl border border-[#E5DFD5] bg-white text-sm text-stone-500">
        Aucun média sur cette annonce.
      </div>
    );
  }

  const actuel = medias[index];
  const estVideo = actuel.type === "video";
  const videoVerrouillee = estVideo && actuel.verrouille;
  const photoVerrouillee = !estVideo && (floute || actuel.verrouille);
  // L'extrait vidéo se lit librement ; l'appel à l'abonnement n'arrive qu'à la fin.
  const verrouille = photoVerrouillee || (videoVerrouillee && finExtrait);
  const photosBloquees = medias.filter((m) => m.type !== "video" && (floute || m.verrouille)).length;
  const videoBloquee = medias.some((m) => m.type === "video" && m.verrouille);

  return (
    <div data-testid={testid} className="space-y-3">
      <div
        className="relative flex h-[min(70vh,600px)] items-center justify-center overflow-hidden rounded-2xl border border-[#E5DFD5] bg-black"
        onTouchStart={(e) => (depart.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (depart.current === null) return;
          const delta = e.changedTouches[0].clientX - depart.current;
          if (Math.abs(delta) > 50) aller(index + (delta < 0 ? 1 : -1));
          depart.current = null;
        }}
      >
        {actuel.type !== "video" && (
          /* Fond flou de la même image : comble les marges de `contain` sans rogner. */
          <img
            key={`fond-${actuel.url}`}
            src={actuel.url}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-2xl"
          />
        )}

        <AnimatePresence mode="wait">
          {actuel.type === "video" ? (
            <motion.video
              key={actuel.url}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              src={actuel.url}
              controls
              onEnded={() => videoVerrouillee && setFinExtrait(true)}
              data-testid={`${testid}-video`}
              className="block h-auto max-h-full w-auto max-w-full bg-black object-contain"
            />
          ) : (
            <motion.img
              key={actuel.url}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              src={actuel.url}
              alt={`Photo ${index + 1}`}
              data-testid={`${testid}-image`}
              className={`relative z-[1] block h-auto max-h-full w-auto max-w-full object-contain object-center transition-[filter] duration-500 ${
                verrouille ? "blur-[16px]" : ""
              }`}
            />
          )}
        </AnimatePresence>

        {verrouille && (
          <>
            <Link
              to={lienAbonnement}
              data-testid={`${testid}-verrou`}
              className="absolute inset-0 z-10 flex cursor-pointer flex-col items-center justify-center gap-2.5 bg-black/45 px-6 text-center transition-colors duration-200 hover:bg-black/55"
            >
              <Lock size={40} className="text-[#D4AF37]" />
              <span className="text-sm font-bold leading-tight text-[#F5E61D]">
                {videoVerrouillee ? (
                  <>
                    Vous avez vu les {actuel.secondesLibres || 5} premières secondes
                    <br />
                    Abonnez-vous pour voir la suite
                  </>
                ) : (
                  <>
                    Prenez un abonnement
                    <br />
                    pour débloquer les images
                  </>
                )}
              </span>
              <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#12100E]">
                {prixDes ? `Débloquer dès ${formaterPrix(prixDes)}` : "Débloquer maintenant"}
              </span>
              {prixDes && (
                <span
                  data-testid={`${testid}-prix`}
                  className="text-[11px] font-medium text-stone-300"
                >
                  Accès 24 h · paiement Mobile Money
                </span>
              )}
            </Link>
          </>
        )}

        {(photoVerrouillee || videoVerrouillee) && (
          <span
            data-testid={`${testid}-badge-premium`}
            className="pointer-events-none absolute -right-10 top-5 z-20 w-40 rotate-45 bg-black/40 py-0.5 text-center text-[11px] font-bold tracking-wider text-[#D9C84A]"
          >
            PREMIUM
          </span>
        )}

        {videoVerrouillee && !finExtrait && (
          <span
            data-testid={`${testid}-extrait-badge`}
            className="pointer-events-none absolute left-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-black/65 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#D4AF37]"
          >
            <Lock size={12} /> Extrait de {actuel.secondesLibres || 5} s
          </span>
        )}

        {total > 1 && (
          <>
            <button
              onClick={() => aller(index - 1)}
              aria-label="Image précédente"
              data-testid={`${testid}-precedent`}
              className="absolute left-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white/85 p-2.5 text-stone-900 shadow-lg transition-transform hover:scale-110"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => aller(index + 1)}
              aria-label="Image suivante"
              data-testid={`${testid}-suivant`}
              className="absolute right-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white/85 p-2.5 text-stone-900 shadow-lg transition-transform hover:scale-110"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}

        <span className="absolute bottom-3 left-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
          <Images size={12} /> {index + 1} / {total}
        </span>
      </div>

      {total > 1 && (
        <div className="flex gap-2.5 overflow-x-auto pb-1.5" data-testid={`${testid}-vignettes`}>
          {medias.map((m, i) => (
            <button
              key={m.url}
              onClick={() => setIndex(i)}
              data-testid={`${testid}-vignette-${i}`}
              aria-label={`Voir le média ${i + 1}`}
              className="relative shrink-0 overflow-hidden rounded-xl transition-transform hover:scale-[1.06]"
              style={{
                boxShadow: i === index ? `0 0 0 3px ${couleur || "#12100E"}` : "0 0 0 1px #E5DFD5",
              }}
            >
              {m.type === "video" ? (
                <video src={m.url} muted className="h-20 w-20 object-cover" />
              ) : (
                <img
                  src={m.url}
                  alt=""
                  className={`h-20 w-20 object-cover ${floute || m.verrouille ? "blur-[6px]" : ""}`}
                />
              )}
              {(floute || m.verrouille) && (
                <span
                  data-testid={`${testid}-vignette-verrou-${i}`}
                  className="absolute inset-0 flex items-center justify-center bg-black/35"
                >
                  <Lock size={16} className="text-[#D4AF37]" />
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {(photosBloquees > 0 || videoBloquee) && (
        <Link
          to={lienAbonnement}
          data-testid={`${testid}-compteur-verrouilles`}
          className="flex items-center justify-between gap-3 rounded-2xl border border-[#D4AF37]/40 bg-[#12100E] px-4 py-3 transition-colors duration-200 hover:bg-[#1C1813]"
        >
          <span className="flex items-center gap-2.5 text-sm font-semibold text-[#F5E61D]">
            <Lock size={16} className="shrink-0 text-[#D4AF37]" />
            {[
              photosBloquees > 0 && `${photosBloquees} photo${photosBloquees > 1 ? "s" : ""}`,
              videoBloquee && "la vidéo complète",
            ]
              .filter(Boolean)
              .join(" et ")}{" "}
            réservée{photosBloquees > 1 || (photosBloquees && videoBloquee) ? "s" : ""} aux abonnés
          </span>
          <span className="shrink-0 rounded-full bg-[#D4AF37] px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-[#12100E]">
            {prixDes ? `Dès ${formaterPrix(prixDes)}` : "Débloquer"}
          </span>
        </Link>
      )}
    </div>
  );
};
